import { useState } from "react";

import { MuiTypography } from "@/components/ui/mui-typography";
import { Notification } from "@/components/ui/notification";
import { overTargetCallout } from "@/lib/target-cost-display";

// The spec pages' target-cost toast (#1216) — the donor's
// `TargetCostNotification` (`shared/components/target-cost-notification.tsx`)
// over react-ui's `Notification`, mounted by the details page and the editor
// for an editor on a community spec while that page's switch is on (#1165
// Q18).
//
// **Its life is its own.** It opens when it mounts, and closes 4000 ms after
// it opens on the app's toast clock — the shared snackbar's `autoHideDuration`,
// which pauses under the pointer and while the window is away and resumes at
// half the duration, as the app's other toasts do. The donor ran its own
// `setTimeout` on `isOpen` instead, so the toast closed under a reader's
// pointer; the owner ruled the app's clock (#1241 D4, a named break). A moved
// count — which never changes `open` — never restarts it. The X closes it sooner (`closable`);
// Escape and a click elsewhere do nothing, as the shared `Notification` rules.
// A page re-fires it by mounting it again (an arrival, a details ↔ edit move,
// a switch-on), and a switch-off unmounts it.
//
// **Its count is the caller's live one**, the spec summary's
// `overTargetCount`, so a mutation result that moves it re-renders an open
// toast in place (PB-T6).
//
// **Its place** is top-centre, 24px under the headers (`SpecDetailsIsland
// .tsx:293-303`, `SpecEditIsland.tsx:306-316`): `calc(var(--app-header-height)
// + 24px)`, whose one variable measured a header holding both the app bar and
// the spec header (144px on local Modsy, so `top: 168px`). Atlas's spec header
// is its own band under the app bar, publishing `--spec-header-height`, so the
// two add up here, as the drawer's do (#951).

/** The donor's own close (`target-cost-notification.tsx`, "Auto-close"), as
 *  the clock's duration. */
export const TARGET_COST_NOTIFICATION_MS = 4000;

const ANCHOR_OFFSET = {
  top: "calc(var(--app-header-height, 0px) + var(--spec-header-height, 0px) + 24px)",
};

interface TargetCostNotificationProps {
  /** The spec summary's `overTargetCount`; `null` reads as 0. */
  count: number | null;
}

export function TargetCostNotification({ count }: TargetCostNotificationProps) {
  const [open, setOpen] = useState(true);

  const callout = overTargetCallout(count);

  return (
    <Notification
      open={open}
      autoHideDuration={TARGET_COST_NOTIFICATION_MS}
      severity={callout.severity}
      anchorOrigin={{ vertical: "top", horizontal: "center" }}
      anchorOffset={ANCHOR_OFFSET}
      closable
      onClose={() => setOpen(false)}
      // `'& .MuiAlert-icon': { color: 'inherit' }`: the icon in the alert's ink.
      iconClassName="text-inherit"
    >
      <MuiTypography variant="body2">
        {callout.severity === "warning" ? (
          <>
            <strong>{callout.count}</strong> {callout.sentence} <em>{callout.note}</em>
          </>
        ) : (
          callout.message
        )}
      </MuiTypography>
    </Notification>
  );
}
